import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import {
    CheckboxComponent,
    DialogService,
    ErrorsUtil,
    MessageBoxComponent,
    OnEnterDirective,
    PasswordUpdateComponent,
    WaitButtonComponent,
    WaitComponent
} from '@sircon/sircon-ui-framework';
import { EnvService } from 'src/services/env.service';
import { AccountHttpService } from 'src/providers/account-http.service';
import { PasswordHttpService } from 'src/providers/password-http.service';
import { TermsOfService, TermsOfServiceHttpService } from 'src/providers/terms-of-service-http.service';
import { UserHttpService } from 'src/providers/user-http.service';
import { GoogleAnalyticsService } from 'src/services/google-analytics.service';
import { InviteSignupService } from 'src/components/invite-signup/invite-signup.service';
import { NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LodashHelperService } from 'src/services/lodash-helper.service';

@Component({
    selector: 'app-reset-password',
    templateUrl: './reset-password.component.html',
    styleUrls: ['./reset-password.component.less'],
    imports: [RouterLink, NgIf, WaitComponent, OnEnterDirective, MessageBoxComponent, PasswordUpdateComponent,
        FormsModule, CheckboxComponent, WaitButtonComponent]
})
export class ResetPasswordComponent implements OnInit {

    doneLoading: boolean;
    errors;
    newPassword: any = {};
    token;
    userId;
    validToken: boolean;
    signedTermsOfService: boolean;
    showLinkToTryAgain: boolean;
    terms;
    submitting: boolean;
    termsAccepted;
    termsId;
    account;
    accountRecovery;
    title = 'Reset Password';
    inviteId = null;

    constructor(
        private cookieService: CookieService,
        private dialogService: DialogService,
        private envService: EnvService,
        private router: Router,
        private accountHttpService: AccountHttpService,
        private passwordHttpService: PasswordHttpService,
        private termsOfServiceHttpService: TermsOfServiceHttpService,
        private userHttpService: UserHttpService,
        private route: ActivatedRoute,
        private window: Window,
        private googleAnalyticsService: GoogleAnalyticsService,
        private inviteSignupService: InviteSignupService,
        private lodashHelperService: LodashHelperService
    ) {}

    ngOnInit() {
        this.doneLoading = false;
        this.errors = ErrorsUtil.create();
        this.newPassword = {};
        this.token = this.route.snapshot.queryParams.token;
        this.userId = this.route.snapshot.queryParams.userId;
        this.accountRecovery = this.route.snapshot.queryParams.accountRecovery;
        this.validToken = false;
        this.inviteId = this.inviteSignupService.getInvite()?.inviteId;

        if (!this.userId || !this.token) {
            this.router.navigate(['/error']);
        }

        // Set the notification token cookie
        this.cookieService.set(this.envService.storageKeys.NOTIFICATION_TOKEN, this.token, this.envService.COOKIES_OPTIONS);
        this.cookieService.delete(this.envService.storageKeys.USER_AUTH, this.envService.COOKIES_OPTIONS.path,
            this.envService.COOKIES_OPTIONS.domain, this.envService.COOKIES_OPTIONS.secure, this.envService.COOKIES_OPTIONS.sameSite);

        this.validateToken()
            .then(() => {
                return this.userHttpService.getAccountByUserId(this.userId).toPromise();
            })
            .then((response) => {
                this.account = response;
                if (this.accountRecovery) {
                    this.googleAnalyticsService.sendTrackingRoute('accountRecoveryResetPasswordTokenGood');
                }
            })
            .catch((response) => {
                if (!this.doneLoading) {
                    this.errors = ErrorsUtil.mapResponse(response);
                }
            })
            .finally(() => {
                this.doneLoading = true;
            });
    }

    /**
     * Validates the token from the URL to determine if the reset password request is still valid.  Also
     * retrieves signed terms of service for the user because pedge migration can leave accounts without a signed terms.
     *
     * Handled cases:
     * 404 - Invalid token
     * 401 - Token has already been consumed or expired
     */
    validateToken() {
        // Getting the terms will also validate the token.  200 is returned even if the user has not accepted terms.
        return this.userHttpService.getTermsOfService(this.userId).toPromise()
            .then((response: any) => {
                this.validToken = true;
                if (response.terms?.length > 0) {
                    this.signedTermsOfService = true;
                }

                if (!this.signedTermsOfService) {
                    this.getTermsOfServiceId();
                }
            }).catch((response) => {
                if (response.status === 404) {
                    // token is invalid
                    this.router.navigate(['/error']);
                } else if (response.status === 401) {
                    // token has already been consumed or is expired.
                    if (this.accountRecovery) {
                        this.errors.accountRecoveryLoadingError = true;
                        this.title = 'Account Recovery';
                        this.googleAnalyticsService.sendTrackingRoute('accountRecoveryResetPasswordTokenBad');
                    } else {
                        this.errors.loadingError = 'This reset password request is no longer valid.';
                        this.showLinkToTryAgain = true;
                    }
                } else {
                    this.errors.loadingError = 'We\'re sorry, an unexpected error occurred. Please try again later.';
                }
                this.doneLoading = true;
            });
    }

    /**
     * Get the terms of service id for individual terms.  This is needed so the user can accept the terms for that id.
     * Everyone that signs up (firm, rep, individual) has a terms of service and can not get here.  Only
     * migrated Pedge users still need a terms of service so we know they are in individual and
     * so we are retrieving the termsId for individuals.
     */
    getTermsOfServiceId() {
        this.termsOfServiceHttpService.getIndividualTermsOfService().then((response: TermsOfService) => {
            this.terms = response;
        }).catch((/* response*/) => {
            this.errors.loadingError = 'We\'re sorry, an unexpected error occurred. Please try again later.';
        });
    }

    /**
     * Submits a requests to reset a user's password along with accepted terms if needed.
     */
    submit() {
        this.submitting = true;
        ErrorsUtil.clear(this.errors);

        if (this.newPassword.password !== this.newPassword.confirmPassword) {
            this.submitting = false;
            this.errors.fieldErrors.newPassword = ErrorsUtil.buildMessage('Passwords must match');
            this.errors.fieldErrors.confirmNewPassword = ErrorsUtil.buildMessage('Passwords must match');
            return;
        }

        if (!this.signedTermsOfService && !this.termsAccepted) {
            this.errors.fieldErrors.termsAccepted = ErrorsUtil.buildMessage('Please agree to the Terms & Conditions');
            this.submitting = false;
            return;
        }

        // If there is no terms of service, this is a pedge migration and we need to accept terms and then reset the password.
        // Calls must be ordered because resetPassword consumes the token, but the token must be good for terms
        if (this.signedTermsOfService) {
            this.resetSubmit();
        } else {
            this.cookieService.set(this.envService.storageKeys.PLATFORM_TOUR, this.userId, this.envService.COOKIES_OPTIONS);
            // eslint-disable-next-line max-len
            this.userHttpService.acceptTermsOfService(this.userId, {
                termsId: this.termsId,
                termsOfServiceType: 'sircon_indv'
            }).toPromise()
                .then(() => {
                    this.resetSubmit();
                })
                .catch((response) => {
                    this.handleSubmitError(response);
                });
        }
    }

    /**
     * Submit the reset password.  This is always the last step since it consumes the reset token.
     */
    resetSubmit() {
        this.resetPassword()
            .then(() => {
                this.submitting = false;
            })
            .catch((res) => {
                this.handleSubmitError(res);
            });
    }

    resetPassword() {
        if (this.account?.accountType === 'agency' || this.account?.accountType === 'educationProvider') {
            const data = {
                userId: this.userId,
                accountId: this.account.accountId,
                newPassword: this.newPassword.password
            };
            return this.passwordHttpService.resetPasswordForClassicIndustry(data).toPromise()
                .then(() => {
                    this.window.open(this.envService.SIRCON_URL + '/login.jsp?messageKey=passwordUpdated', '_self');
                });
        } else {
            const data = {
                userId: this.userId,
                newPassword: this.newPassword.password
            };
            return this.accountHttpService.resetPassword(data).toPromise()
                .then(() => {
                    let params;
                    if (this.accountRecovery) {
                        this.googleAnalyticsService.sendTrackingRoute('accountRecoveryResetPasswordSuccess');
                    }
                    if (this.inviteId) {
                        params = {
                            passwordUpdated: true,
                            redirectParam: `inviteId=${this.inviteId}`,
                            redirectRoute: 'inviteSignup.processInvite'
                        };
                    } else {
                        params = {
                            passwordUpdated: true
                        };
                    }
                    this.router.navigate(['/login'], { queryParams: params });
                });
        }
    }

    handleSubmitError(response) {
        const responseBody = response.error || {};

        if (response.status === 409 || response.status === 401) {
            // token has already been consumed
            this.validToken = false;
            this.errors.loadingError = 'This reset password request is no longer valid.';
            this.showLinkToTryAgain = true;
        } else if (response.status === 422) {
            this.errors.fieldErrors = responseBody.fieldErrors;
            this.errors.globalErrors = responseBody.globalErrors;
            this.errors.validationErrors = true;
        } else {
            this.errors.unexpectedError = true;
        }
        this.submitting = false;
    }

    isContinueDisabled() {
        return this.lodashHelperService.isEmpty(this.newPassword.password) || this.lodashHelperService.isEmpty(this.newPassword.confirmPassword);
    }

    openTermsOfService() {
        this.termsAccepted = true;
        this.dialogService.open('terms-of-service');
    }
}
